import { dateTimeManager } from "./TimeManager";
import Swal from "sweetalert2";

const mimeTypeToExtensionMap: Record<string, string> = {
    // Image Formats
    "application/pdf": ".pdf",
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/gif": ".gif",
    "image/bmp": ".bmp",
    "image/svg+xml": ".svg",
    "image/tiff": ".tiff",
    "image/webp": ".webp",
    "image/heif": ".heif", // High Efficiency Image File Format
    "image/vnd.microsoft.icon": ".ico",

    // Microsoft Office Formats
    "application/msword": ".doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
    "application/vnd.ms-excel": ".xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
    "application/vnd.ms-powerpoint": ".ppt",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx",
    "application/vnd.ms-access": ".mdb",
    "text/csv": ".csv",

    // Text Formats
    "text/plain": ".txt",
    "text/html": ".html",
    "text/css": ".css",
    "text/javascript": ".js",
    "application/json": ".json",
    "text/xml": ".xml",
    "text/markdown": ".md",
    "application/x-yaml": ".yaml",
};

type ArrayPredicate<T> = (value: T, index: number, array: T[]) => boolean;

/**
 * Shared formatting, loading-overlay, and MIME-type helpers for the Yango Report App.
 * Exposed on `window.$utils` and `Vue.prototype.$utils` by GlobalVariables.ts.
 */
export const utils = {
    /**
     * Converts a string to PascalCase by capitalising the first letter of each word.
     *
     * @param string - Raw input string (any casing).
     * @returns The PascalCase form of the input.
     */
    toPascalCase(string: string): string {
        return string
            .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
            .replace(/^\w/, s => s.toUpperCase());
    },

    /**
     * Truncates a string to a maximum length and appends an ellipsis when exceeded.
     *
     * @param string - Source text to shorten.
     * @param limit - Maximum character count before truncation (default 45).
     * @returns The original string when shorter than `limit`, otherwise a truncated prefix with `...`.
     */
    cutLength(string: string, limit: number = 45): string {
        if (string.length < limit) {
            return string;
        } else {
            return string.substring(0, limit).concat('...');
        }
    },

    /**
     * Formats a numeric amount as a locale-style money string with optional symbol prefix.
     *
     * @param amount - Monetary value as number or numeric string.
     * @param decimalCount - Number of fractional digits (default 2).
     * @param decimal - Decimal separator character (default `.`).
     * @param thousands - Thousands grouping separator (default `,`).
     * @param symbol - Currency symbol prefix (default `K`).
     * @returns Formatted money string, or `undefined` when formatting throws.
     */
    formatMoney(
        amount: number | string,
        decimalCount: number = 2,
        decimal: string = ".",
        thousands: string = ",",
        symbol: string = "K"
    ): string | undefined {
        try {
            decimalCount = Math.abs(decimalCount);
            decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

            const negativeSign: string = Number(amount) < 0 ? "-" : "";

            const fixedAmount: string = Math.abs(Number(amount) || 0).toFixed(decimalCount);
            const i: string = parseInt(fixedAmount).toString();
            const j: number = i.length > 3 ? i.length % 3 : 0;

            return (
                `${symbol} ` +
                negativeSign +
                (j ? i.substr(0, j) + thousands : "") +
                i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
                (decimalCount
                    ? decimal +
                    Math.abs(Number(fixedAmount) - Number(i))
                        .toFixed(decimalCount)
                        .slice(2)
                    : "")
            );
        } catch (e) {
            console.error(e);
            return undefined;
        }
    },

    /**
     * Returns a new array containing only elements that satisfy the predicate (alias for `filter`).
     *
     * @param arr - Source array to filter.
     * @param predicate - Inclusion test invoked per element.
     * @returns Filtered copy of `arr`.
     */
    removeIf<T>(arr: T[], predicate: ArrayPredicate<T>): T[] {
        return arr.filter(predicate);
    },

    /**
     * Returns a new array with all occurrences of `obj` removed by strict equality.
     *
     * @param arr - Source array.
     * @param obj - Value to exclude.
     * @returns Copy of `arr` without matching entries.
     */
    remove<T>(arr: T[], obj: T): T[] {
        return arr.filter((v: T) => v !== obj);
    },

    /**
     * Shows a non-dismissible SweetAlert2 loading dialog.
     *
     * @param options - Optional `title` and `html` overrides for the dialog body.
     * @returns Promise resolving to the Swal fire result.
     */
    showLoading(options?: { title?: string; html?: string }): Promise<unknown> {
        return Swal.fire({
            icon: "info",
            title: options?.title ?? 'Please wait...',
            html: options?.html ?? 'Processing your request...',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });
    },

    /**
     * Closes any open SweetAlert2 dialog (typically the global loading overlay).
     *
     * @returns void
     */
    hideLoading(): void {
        Swal.close();
    },

    /**
     * Resolves a file extension for a known MIME type string.
     *
     * @param mimeType - IANA MIME type (e.g. `image/png`).
     * @returns Dotted extension including the leading dot, or empty string when unknown.
     */
    getExtensionByMimeType(mimeType: string): string {
        return mimeTypeToExtensionMap[mimeType] || "";
    },

    /**
     * Resolves a MIME type for a known dotted file extension.
     *
     * @param extension - Dotted extension (e.g. `.xlsx`).
     * @returns MIME type string, or empty string when unknown.
     */
    getMimeTypeByExtension(extension: string): string {
        const extensionToMimeTypeMap: Record<string, string> = Object.fromEntries(
            Object.entries(mimeTypeToExtensionMap).map(([mimeType, ext]) => [ext, mimeType])
        );
        return extensionToMimeTypeMap[extension] || "";
    },

    /**
     * Formats an ISO-style date string for short UI display via dateTimeManager.
     *
     * @param date - Parseable date-time string.
     * @returns Short display label, or empty string when input cannot be formatted.
     */
    displayDate(date: string): string {
        return dateTimeManager.shortenStandardDisplay(date) ?? "";
    },
};